import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { makeListResponse, makePaginatedResponse } from '~/mocks/utils/listFixtures';
import { axiosInstance } from '~/services';

import { RECEIVE_REQUEST_STATUS, RELEASE_CODE, TypeReceivePageSize } from '~/constants';

import { receiveKeys } from './keys';

import type { TypeReceiveRequestsInfo } from '~/types/receive.types';
import type { TypePostRequestsRequestedParams, TypePostRequestsRequestedResponse } from '~/types/receive.types';

const fetchRequestsRequested = async (
  ownerCode: string,
  params: TypePostRequestsRequestedParams
): Promise<TypePostRequestsRequestedResponse> => {
  const { data } = await axiosInstance.post<TypePostRequestsRequestedResponse>(
    `/api-inbound/v1/3pl/receiving/${ownerCode}/requests/requested`,
    params
  );
  return data;
};

type TypeUsePostRequestsRequestedQueryParams = {
  ownerCode: string;
  params: TypePostRequestsRequestedParams;
  enabled?: boolean;
};

/**
 * [입고관리 > 입고요청관리] 입고요청 목록 조회 Query Hook
 */
export const usePostRequestsRequestedQuery = ({
  ownerCode,
  params,
  enabled = true,
}: TypeUsePostRequestsRequestedQueryParams) => {
  return useQuery({
    queryKey: receiveKeys.requestsRequested(ownerCode, params),
    queryFn: () => fetchRequestsRequested(ownerCode, params),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
  });
};

const STATUSES = Object.keys(RECEIVE_REQUEST_STATUS) as Array<keyof typeof RECEIVE_REQUEST_STATUS>;
// KURLY_MILKRUN_PICKUP은 BE 응답이 아닌 FE 표시용 파생 코드라 제외
const RELEASE_CODES = (Object.keys(RELEASE_CODE) as Array<keyof typeof RELEASE_CODE>).filter(
  (code) => code !== 'KURLY_MILKRUN_PICKUP'
);
const WAREHOUSES: Array<{ code: string; name: string }> = [
  { code: 'GPH1', name: '평택상온' },
  { code: 'GGH1', name: '김포상온' },
  { code: 'GGM1', name: '김포냉장' },
  { code: 'WH01_O', name: '장지상온' },
];

const makeRequest = (index: number, override: Partial<TypeReceiveRequestsInfo> = {}): TypeReceiveRequestsInfo => {
  const warehouse = WAREHOUSES[index % WAREHOUSES.length];
  return {
    requestId: 8000 + index,
    requestCode: `OR20260507_${String(index).padStart(5, '0')}`,
    status: STATUSES[index % STATUSES.length],
    warehouse: warehouse.code,
    warehouseName: warehouse.name,
    xdockYn: index % 3 === 0,
    releaseCode: RELEASE_CODES[index % RELEASE_CODES.length],
    receivingExpectedDate: '2026-05-08',
    receivingExpectedTime: '12:00~14:00',
    supplierName: '테스트 화주사_첫번째 공급사',
    supplierCode: 'SU000004',
    skuCnt: 1 + (index % 5),
    unitQty: 10 + index * 10,
    requestedDate: '2026-05-07T10:17:33',
    ...override,
  };
};

const buildListResponse = (count: number): TypePostRequestsRequestedResponse =>
  makeListResponse(count, (index) => makeRequest(index), 'receivingRequests', {
    listView: 50,
    listTotal: count,
    cursor: 0,
  });

// 상태·날짜 일괄 override 시나리오 — 액션 분기(취소/라벨/명세서) e2e 검증용.
// receivingExpectedDate를 함수형(today 기준 동적)으로 생성해 cutoff 검증 가능.
const buildUniformStatusResponse = (
  status: keyof typeof RECEIVE_REQUEST_STATUS,
  receivingExpectedDate: string,
  count = 5
): TypePostRequestsRequestedResponse =>
  makeListResponse(count, (index) => makeRequest(index, { status, receivingExpectedDate }), 'receivingRequests', {
    listView: 50,
    listTotal: count,
    cursor: 0,
  });

registerMockAPI<TypePostRequestsRequestedResponse>({
  method: 'post',
  endpoint: '/api-inbound/v1/3pl/receiving/:ownerCode/requests/requested',
  label: '입고/입고요청/요청목록',
  scenarios: [
    { name: '정상 응답 — 5건', response: buildListResponse(5) },
    { name: '정상 응답 — 0건 (빈 목록)', response: buildListResponse(0) },
    {
      name: '정상 응답 — 250건 (페이지네이션)',
      response: makePaginatedResponse(250, makeRequest, 'receivingRequests', ({ limit, offset, total }) => ({
        listView: limit as TypeReceivePageSize,
        listTotal: total,
        cursor: offset,
      })),
    },
    {
      name: '잘못된 파라미터 (9001)',
      response: { error: { code: 9001, message: 'Error Occured By Not Valid Parameters' } },
      status: 400,
    },
    { name: '정상 응답 — 3초 지연', response: buildListResponse(5), delay: 3000 },
    // 액션 분기 e2e 검증용 — 상태·cutoff 일괄
    {
      name: '정상 응답 — 전건 요청완료 5건',
      response: () => buildUniformStatusResponse('REQUESTED', dayjs().add(3, 'day').format('YYYY-MM-DD')),
    },
    {
      name: '정상 응답 — 전건 확인완료 5건 (cutoff 전)',
      response: () => buildUniformStatusResponse('CONFIRMED', dayjs().add(3, 'day').format('YYYY-MM-DD')),
    },
    {
      name: '정상 응답 — 전건 확인완료 5건 (cutoff 후)',
      response: () => buildUniformStatusResponse('CONFIRMED', dayjs().format('YYYY-MM-DD')),
    },
    {
      name: '정상 응답 — 전건 요청취소 5건',
      response: () => buildUniformStatusResponse('CANCELED', dayjs().add(3, 'day').format('YYYY-MM-DD')),
    },
  ],
});
